import { PermissionEnum } from '@Application/Security/Roles';
import DeleteIdentityProvider from '@Application/Security/Sso/Commands/DeleteIdentityProvider';
import deleteIdentityProvider from '@Application/Security/Sso/deleteIdentityProvider';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { setSsoCreationOpen } from '@UI/Pages/Administration/Sso/Signals/ssoCreationSignal';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly accountId: number;
};

/** Guards, confirms and performs the destructive deletion of the identity provider configuration,
 * dispatching it itself and closing the creation form via the shared signal. */
const DeleteConfigurationButton: React.FC<Props> = ({ accountId }) => {
	const dispatch = useDispatch();
	const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = React.useState(false);

	const handleOpenDeleteConfirm = React.useCallback(() => {
		setIsDeleteConfirmVisible(true);
	}, []);

	const handleDeleteConfirmHide = React.useCallback(() => {
		setIsDeleteConfirmVisible(false);
	}, []);

	const handleDeleteAccept = React.useCallback(() => {
		dispatch(deleteIdentityProvider(new DeleteIdentityProvider(accountId)))
			.then(() => setSsoCreationOpen(false))
			.catch(ignoreHandledError);
		// Closes immediately: `acceptCallback`/`accept` never triggers `onHide`, and waiting for the
		// request to settle would leave the dialog open long enough for a second click.
		setIsDeleteConfirmVisible(false);
	}, [accountId, dispatch]);

	return (
		<React.Fragment>
			<WithPermission permission={PermissionEnum.SSO_MANAGEMENT}>
				<Button
					type="button"
					text
					severity="danger"
					label="Supprimer la configuration"
					onClick={handleOpenDeleteConfirm}
				/>
			</WithPermission>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isDeleteConfirmVisible}
				onHide={handleDeleteConfirmHide}
				header="Supprimer la configuration"
				message="La configuration sera supprimée et les connexions par fournisseur d’identité cesseront immédiatement. Le secret client devra être ressaisi pour la rétablir."
				acceptLabel="Supprimer"
				acceptSeverity="danger"
				accept={handleDeleteAccept}
			/>
		</React.Fragment>
	);
};

export default DeleteConfigurationButton;
